/**
 * RecordingsContext - State management for user recordings/sessions
 *
 * Features:
 * - Uses EntityReducer for consistent state updates
 * - Subscribes to EventBus for real-time updates
 * - Handles sync event for initial state
 * - Maintains backward compatibility with existing API
 */

import { createContext, ReactNode, useContext, useEffect, useState, useCallback, useRef } from "react";
import { Optional, RecordingNote, RecordingNoteContent } from "../utils/types.ts";
import {
  deleteRecording,
  generateNewRecordingNote,
  getFullRecording,
  getRecordingNote,
  getUserSessions,
  updateSession
} from "../api/recordings.ts";
import { Patient } from "./PatientsContext.tsx";
import { useTranslation } from "react-i18next";
import { SupportedLanguage } from "../utils/i18n.ts";
import { ORGANIZATION_SWITCHED_EVENT, OrganizationContext } from "./OrganizationContext.tsx";
import {
  getEventBus,
  EventTypes,
  RecordingPayload,
  NotePayload,
  PatientRecordingPayload,
  SyncPayload,
} from "../services/EventBus.ts";
import {
  entityReducer,
  EntityAction,
} from "../services/EntityReducer.ts";

export type RecordingStatus = 'in_progress' | 'completed' | 'failed' | 'needs_fetch' | 'completed_no_transcription';
export type SpeakerRole = 'patient' | 'clinician' | 'other';

export type Word = {
  text: string,
  offsetMilliseconds: number,
  durationMilliseconds: number
}

export type Locale = 'en-US';

export type Phrase = {
  text: string,
  words: Word[],
  locale: Locale,
  speaker: number,
  confidence: number,
  offsetMilliseconds: number,
  durationMilliseconds: number
}

export type Speaker = {
  id: number,
  speaker_id: number,
  role: SpeakerRole,
  name: Optional<string>,
  recording_id: number,
}

export type Transcript = {
  phrases: Phrase[],
  duration_in_milliseconds: number,
  speakers: Speaker[],
}

export type RecordingUnparsed = {
  id: number,
  organization_id: number,
  transcription: Optional<string>,
  transcription_engine: Optional<string>,
  transcription_engine_version: Optional<string>,
  transcribed_at: Optional<string>,
  created_at: string,
  updated_at: string,
  account_id: Optional<number>,
  session_id: number,
  status: Optional<RecordingStatus>,
  error: Optional<string>,
  duration_milliseconds: Optional<number>,
  title: Optional<string>,
  has_audio?: boolean,
  audio_url?: Optional<string>,
  patient: Optional<Patient>,
  current_note: Optional<RecordingNote>,
  pending_ack: boolean,
}

export type Recording = RecordingUnparsed & {
  transcription: Optional<Transcript>,
  filled?: boolean,
};

export type WebsocketRecordingPayload = Recording & {
  transcription: null,
  transcription_engine: null,
  transcription_engine_version: null,
  transcribed_at: null,
}

export type WebsocketRecordingNotePayload = {
  id: number,
  status: string,
  error: Optional<string>,
  finished_at: Optional<string>,
  content: Optional<string | RecordingNoteContent>,
  note_type_version: number,
  recording_id: number,
  created_at: string,
  updated_at: string,
  note_type_id: number,
  language: SupportedLanguage,
}

export type RecordingContextType = {
  recordings: Optional<Recording[]>;
  setRecordings: (user: Optional<Recording[]>) => void;
  loading: boolean;
  activeRecording: Optional<Recording>;
  setActiveRecording: (sessionId: Optional<number>) => void;
  fetchRecordings: (sessionId?: string) => void;
  fetchRecording: (sessionId: number) => Promise<void>;
  fillRecording: (recording: Recording) => void;
  handleAssignPatient: (patient: Patient) => void;
  handleChangeRecordingNoteType: (noteTypeId: number) => void;
  updateSessionFields: (newData: WebsocketRecordingPayload) => void;
  addNewSession: (session: WebsocketRecordingPayload) => void;
  removeSession: (session: WebsocketRecordingPayload) => void;
  deleteSession: (sessionId: number) => Promise<void>;
  updateSessionNote: (newData: WebsocketRecordingNotePayload) => void;
  getSessionNote: (sessionId: number) => Promise<Optional<RecordingNote>>;
  prependNewSession: (duration: number) => string;
  replaceSession: (date: string, recording: RecordingUnparsed) => void;
};

const contextDefaultValues: RecordingContextType = {
  recordings: null,
  setRecordings: () => {},
  loading: false,
  activeRecording: null,
  setActiveRecording: () => {},
  fetchRecordings: async () => {},
  fetchRecording: async () => {},
  fillRecording: () => {},
  handleAssignPatient: () => {},
  handleChangeRecordingNoteType: () => {},
  updateSessionFields: () => {},
  addNewSession: () => {},
  deleteSession: () => Promise.resolve(),
  removeSession: () => {},
  updateSessionNote: () => {},
  getSessionNote: () => Promise.resolve(null),
  prependNewSession: () => '',
  replaceSession: () => {}
}

const RecordingsContext = createContext<RecordingContextType>(contextDefaultValues);

/**
 * Parse transcription to Transcript object
 * Handles: JSON string (from some APIs), object (from WebSocket), or null
 */
function parseTranscription(transcription: unknown): Optional<Transcript> {
  if (!transcription) return null;

  // If it's already an object, use it directly
  if (typeof transcription === 'object') {
    return transcription as Transcript;
  }

  // If it's a string, parse it as JSON (throws on invalid JSON)
  if (typeof transcription === 'string') {
    return JSON.parse(transcription) as Transcript;
  }

  throw new Error(`Unexpected transcription type: ${typeof transcription}`);
}

/**
 * Normalize a recording payload from API or WebSocket to Recording type
 */
function normalizeRecording(payload: RecordingPayload | RecordingUnparsed): Recording {
  const transcription = parseTranscription(payload.transcription);

  // Handle both duration_milliseconds (from server) and existing duration_milliseconds
  const durationMs = (payload as RecordingUnparsed).duration_milliseconds
    ?? (payload as RecordingPayload).duration_milliseconds
    ?? null;

  return {
    ...payload,
    transcription,
    duration_milliseconds: durationMs,
    status: payload.status as RecordingStatus ?? null,
    patient: payload.patient as Patient ?? null,
    current_note: (payload as RecordingUnparsed).current_note ?? null,
    pending_ack: (payload as RecordingUnparsed).pending_ack ?? false,
  } as Recording;
}

/**
 * Sort function for recordings (newest first)
 */
const sortRecordings = (a: Recording, b: Recording) =>
  new Date(b.created_at).getTime() - new Date(a.created_at).getTime();

function RecordingsProvider({ children }: { children: ReactNode }) {
  const [recordings, setRecordings] = useState<Optional<Recording[]>>(null);
  const [activeRecording, setActiveRecording] = useState<Optional<Recording>>(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { currentOrganization } = useContext(OrganizationContext);
  const eventBus = getEventBus();

  // Track if initial data was loaded via sync
  const initialLoadDone = useRef(false);

  /**
   * Apply entity action to recordings state
   */
  const dispatchRecordingAction = useCallback((action: EntityAction<Recording>) => {
    setRecordings(prev => {
      if (prev === null) {
        if (action.type === 'SET_ALL') {
          return action.entities.sort(sortRecordings);
        }
        // Initialize with the single entity if we get an UPSERT before SET_ALL
        if (action.type === 'UPSERT') {
          return [action.entity];
        }
        // For REMOVE on null state, nothing to do
        if (action.type === 'REMOVE') {
          return null;
        }
        throw new Error(`Unexpected action type "${action.type}" when recordings is null`);
      }
      return entityReducer(prev, action, sortRecordings);
    });
  }, []);

  function updateRecordingStatus(recordingId: number, status: RecordingStatus) {
    setRecordings(prev => {
      if (!prev) return prev;
      return prev.map(r => r.id === recordingId ? { ...r, status } : r);
    });
    setActiveRecording(prev => {
      if (prev && prev.id === recordingId) {
        return { ...prev, current_note: null, status };
      }
      return prev;
    });
  }

  // Subscribe to EventBus for real-time updates
  useEffect(() => {
    const subscriptions = [
      // Full state sync
      eventBus.subscribe(EventTypes.SYNC, (payload: SyncPayload) => {
        const normalizedRecordings = payload.recordings.map(r => normalizeRecording(r as unknown as RecordingPayload));
        dispatchRecordingAction({ type: 'SET_ALL', entities: normalizedRecordings });
        initialLoadDone.current = true;
      }),

      // New recording created
      eventBus.subscribe(EventTypes.NEW_RECORDING, (payload: RecordingPayload) => {
        const recording = normalizeRecording(payload);
        dispatchRecordingAction({ type: 'UPSERT', entity: recording });
      }),

      // Recording completed (transcription ready)
      eventBus.subscribe(EventTypes.RECORDING_COMPLETED, (payload: RecordingPayload) => {
        const recording = normalizeRecording(payload);
        dispatchRecordingAction({ type: 'UPSERT', entity: recording });

        // Update active recording if it matches
        setActiveRecording(prev => {
          if (prev && prev.id === recording.id) {
            return { ...prev, ...recording, status: 'needs_fetch' };
          }
          return prev;
        });
      }),

      // Recording failed
      eventBus.subscribe(EventTypes.RECORDING_FAILED, (payload: RecordingPayload) => {
        const recording = normalizeRecording(payload);
        dispatchRecordingAction({ type: 'UPSERT', entity: recording });
      }),

      // Recording pending acknowledgment
      eventBus.subscribe(EventTypes.RECORDING_PENDING_ACK, (payload: RecordingPayload) => {
        const recording = normalizeRecording(payload);
        dispatchRecordingAction({ type: 'UPSERT', entity: recording });
      }),

      // Recording acknowledged
      eventBus.subscribe(EventTypes.RECORDING_ACKED, (payload: RecordingPayload) => {
        const recording = normalizeRecording(payload);
        dispatchRecordingAction({ type: 'UPSERT', entity: recording });
      }),

      // Recording deleted
      eventBus.subscribe(EventTypes.RECORDING_DELETED, (payload) => {
        dispatchRecordingAction({ type: 'REMOVE', id: payload.id });
        setActiveRecording(prev => {
          if (prev && prev.id === payload.id) {
            return null;
          }
          return prev;
        });
      }),

      // Note events
      eventBus.subscribe(EventTypes.NEW_NOTE, (payload: NotePayload) => {
        updateRecordingStatus(payload.recording_id, 'needs_fetch');
      }),

      eventBus.subscribe(EventTypes.NOTE_COMPLETED, (payload: NotePayload) => {
        updateRecordingStatus(payload.recording_id, 'needs_fetch');
      }),

      eventBus.subscribe(EventTypes.NOTE_FAILED, (payload) => {
        updateRecordingStatus(payload.recording_id, 'needs_fetch');
      }),

      // Patient assignment events
      eventBus.subscribe(EventTypes.PATIENT_ASSIGNED_TO_RECORDING, (payload: PatientRecordingPayload) => {
        setRecordings(prev => {
          if (!prev) return prev;
          return prev.map(r => {
            if (r.id === payload.recording_id) {
              // Will need to fetch the patient details
              return { ...r, status: 'needs_fetch' as RecordingStatus };
            }
            return r;
          });
        });
      }),

      eventBus.subscribe(EventTypes.PATIENT_UNASSIGNED_FROM_RECORDING, (payload: PatientRecordingPayload) => {
        setRecordings(prev => {
          if (!prev) return prev;
          return prev.map(r => {
            if (r.id === payload.recording_id) {
              return { ...r, patient: null };
            }
            return r;
          });
        });
      }),
    ];

    return () => {
      subscriptions.forEach(sub => sub.unsubscribe());
    };
  }, [eventBus, dispatchRecordingAction]);

  // Listen for organization switch events and clear data
  useEffect(() => {
    function handleOrganizationSwitch() {
      setRecordings(null);
      setActiveRecording(null);
      initialLoadDone.current = false;
    }

    window.addEventListener(ORGANIZATION_SWITCHED_EVENT, handleOrganizationSwitch);
    return () => {
      window.removeEventListener(ORGANIZATION_SWITCHED_EVENT, handleOrganizationSwitch);
    };
  }, []);

  async function handleDeleteSession(sessionId: number) {
    if (!recordings) return;
    const index = recordings.findIndex(s => s.id === sessionId);
    if (index === -1) return;
    await deleteRecording(sessionId);
    dispatchRecordingAction({ type: 'REMOVE', id: sessionId });
    if (activeRecording && activeRecording.id === sessionId) {
      setActiveRecording(null);
    }
  }

  function handleSetActiveSession(sessionId: Optional<number>) {
    if (!sessionId) {
      setActiveRecording(null);
      return;
    }
    const session = recordings?.find(s => s.id === sessionId);
    if (session) {
      setActiveRecording(session);
    }
  }

  async function fetchRecordings(sessionId?: string) {
    // Skip if already loaded via sync
    if (recordings || initialLoadDone.current) return;

    setLoading(true);
    const response = await getUserSessions();
    const parsedResponse: Recording[] = response.map(s => normalizeRecording(s))
      .sort(sortRecordings);

    setRecordings(parsedResponse);
    setLoading(false);

    if (parsedResponse.length > 0) {
      if (activeRecording) {
        const index = parsedResponse.findIndex(s => s.id === activeRecording.id);
        setActiveRecording(parsedResponse[index]);
      } else if (sessionId) {
        const index = parsedResponse.findIndex(s => s.id === Number(sessionId));
        if (index !== -1) {
          setActiveRecording(parsedResponse[index]);
        } else {
          const firstWithStatusComplete = parsedResponse.find(s => s.status === 'completed')!;
          setActiveRecording(firstWithStatusComplete);
        }
      } else {
        const firstWithStatusComplete = parsedResponse.find(s => s.status === 'completed')!;
        setActiveRecording(firstWithStatusComplete);
      }
    }
  }

  function fillRecording(recording: Recording) {
    recording['filled'] = true;
    dispatchRecordingAction({ type: 'UPSERT', entity: recording });
    setActiveRecording(recording);
  }

  async function handleAssignPatient(patient: Patient) {
    if (!activeRecording) throw new Error(t('errors.noActiveRecording'));
    if (!patient || !recordings) return;
    const response = await updateSession(activeRecording.id, { patient_id: patient.id });
    const recording = normalizeRecording(response);
    dispatchRecordingAction({ type: 'UPSERT', entity: recording });
    setActiveRecording(recording);
  }

  async function handleChangeRecordingNoteType(noteTypeId: number) {
    if (!activeRecording) throw new Error(t('errors.noActiveRecording'));
    if (!recordings) return;
    // Immediately show in_progress state before the API call returns
    const pendingRecording: Recording = {
      ...activeRecording,
      status: 'in_progress',
    } as Recording;
    dispatchRecordingAction({ type: 'UPSERT', entity: pendingRecording });
    setActiveRecording(pendingRecording);
    const response = await generateNewRecordingNote(activeRecording.id, { note_type_id: noteTypeId });
    const recording: Recording = {
      ...activeRecording,
      status: 'in_progress',
      current_note: response,
    } as Recording;
    dispatchRecordingAction({ type: 'UPSERT', entity: recording });
    setActiveRecording(recording);
  }

  async function fetchRecording(sessionId: number): Promise<void> {
    if (!recordings) return;
    if (!recordings.find(s => s.id === sessionId)) throw new Error(t('errors.recordingNotFound'));
    const fullRecording: Recording = await getFullRecording(sessionId);
    const existingRecording = recordings.find(s => s.id === sessionId);
    const merged = { ...existingRecording, ...fullRecording };
    dispatchRecordingAction({ type: 'UPSERT', entity: merged as Recording });
    setActiveRecording(merged as Recording);
  }

  // Legacy function - now handled via EventBus
  function updateSessionFields(newData: WebsocketRecordingPayload) {
    const recording = normalizeRecording(newData as unknown as RecordingPayload);
    dispatchRecordingAction({ type: 'UPSERT', entity: recording });
  }

  // Legacy function - now handled via EventBus
  function addNewSession(session: WebsocketRecordingPayload) {
    const recording = normalizeRecording(session as unknown as RecordingPayload);
    dispatchRecordingAction({ type: 'UPSERT', entity: recording });
  }

  // Legacy function - now handled via EventBus
  function removeSession(session: WebsocketRecordingPayload) {
    dispatchRecordingAction({ type: 'REMOVE', id: session.id });
  }

  // Legacy function - now handled via EventBus
  async function updateSessionNote(newData: WebsocketRecordingNotePayload) {
    setActiveRecording(prev => {
      if (!prev) return null;
      if (prev.id !== newData.recording_id) return prev;
      return {
        ...prev,
        current_note: null,
        status: 'needs_fetch'
      }
    });
  }

  async function getSessionNote(sessionId: number): Promise<Optional<RecordingNote>> {
    if (!recordings) return null;
    const recording = recordings.find(s => s.id === sessionId);
    if (!recording) return null;
    const response = await getRecordingNote(sessionId);
    setActiveRecording(prev => {
      if (!prev) return null;
      if (prev.id !== sessionId) return prev;
      return {
        ...prev,
        current_note: response,
        status: prev.status === 'needs_fetch' ? 'completed' : prev.status
      }
    });
    return response;
  }

  function prependNewSession(duration: number): string {
    const createDate = new Date().toISOString();
    const incompleteSession: Recording = {
      id: -1,
      organization_id: currentOrganization?.id ?? 0,
      transcription: null,
      transcription_engine: null,
      transcription_engine_version: null,
      transcribed_at: null,
      created_at: createDate,
      updated_at: createDate,
      account_id: null,
      session_id: -1,
      status: 'in_progress',
      error: null,
      duration_milliseconds: duration,
      title: null,
      has_audio: false,
      patient: null,
      current_note: null,
      pending_ack: true,
    }
    setRecordings(prev => {
      if (!prev) return [incompleteSession];
      return [incompleteSession, ...prev];
    });
    return createDate;
  }

  function replaceSession(date: string, recording: RecordingUnparsed) {
    setRecordings(prev => {
      if (!prev) return null;
      const newRecordings = [...prev];
      const index = newRecordings.findIndex(s => s.created_at === date);
      if (index === -1) return newRecordings;
      newRecordings[index] = normalizeRecording(recording);
      return newRecordings;
    });
  }

  return <RecordingsContext.Provider value={{
    loading,
    handleChangeRecordingNoteType,
    handleAssignPatient,
    fillRecording,
    recordings,
    setRecordings,
    activeRecording,
    setActiveRecording: handleSetActiveSession,
    fetchRecordings,
    fetchRecording,
    updateSessionFields,
    addNewSession,
    deleteSession: handleDeleteSession,
    removeSession,
    updateSessionNote,
    getSessionNote,
    prependNewSession,
    replaceSession
  }}>{children}</RecordingsContext.Provider>;
}

export { RecordingsContext, RecordingsProvider };
